import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Activity, 
  TrendingUp, 
  TrendingDown,
  DollarSign, 
  AlertTriangle,
  CheckCircle,
  Lightbulb,
  RefreshCw,
  Zap,
  Target,
  PiggyBank,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  Users,
  FileText,
  Sparkles,
  ShieldAlert,
  Banknote,
  Gauge
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function BusinessHealthScore() {
  const [loading, setLoading] = useState(true);
  const [healthData, setHealthData] = useState(null);
  const [insights, setInsights] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [insightsLoading, setInsightsLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [healthRes, alertsRes] = await Promise.all([
        axios.get(`${API_URL}/api/business/health-score`, { withCredentials: true }),
        axios.get(`${API_URL}/api/business/risk-alerts`, { withCredentials: true })
      ]);
      
      setHealthData(healthRes.data);
      setAlerts(alertsRes.data.alerts || []);
    } catch (error) {
      console.error('Error fetching health data:', error);
      toast.error('Failed to load health score');
    } finally {
      setLoading(false);
    }
  };

  const fetchInsights = async () => {
    setInsightsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/business/ai-insights`, { withCredentials: true });
      setInsights(res.data.insights || []);
      toast.success('AI insights generated!');
    } catch (error) {
      console.error('Error fetching insights:', error);
      toast.error('Failed to generate insights');
    } finally {
      setInsightsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-400';
    if (score >= 65) return 'text-blue-400';
    if (score >= 50) return 'text-amber-400';
    if (score >= 35) return 'text-orange-400';
    return 'text-red-400';
  };

  const getScoreGradient = (score) => {
    if (score >= 80) return 'from-emerald-500 to-emerald-400';
    if (score >= 65) return 'from-blue-500 to-blue-400';
    if (score >= 50) return 'from-amber-500 to-amber-400';
    if (score >= 35) return 'from-orange-500 to-orange-400';
    return 'from-red-500 to-red-400';
  };

  const getInsightIcon = (type) => {
    switch (type) {
      case 'warning': return <AlertTriangle className="w-5 h-5 text-amber-400" />;
      case 'opportunity': return <Lightbulb className="w-5 h-5 text-blue-400" />;
      case 'achievement': return <CheckCircle className="w-5 h-5 text-emerald-400" />;
      default: return <Zap className="w-5 h-5 text-purple-400" />;
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'critical': return <ShieldAlert className="w-5 h-5 text-red-500" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      default: return <Clock className="w-5 h-5 text-blue-500" />;
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse text-muted-foreground flex items-center gap-2">
          <Activity className="w-5 h-5 animate-pulse" />
          Analyzing business health...
        </div>
      </div>
    );
  }

  const healthScore = healthData?.health_score || {};
  const runway = healthData?.cash_runway || {};
  const kpis = healthData?.kpis || {};

  return (
    <div className="p-6 space-y-6" data-testid="health-score-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Gauge className="w-7 h-7 text-[hsl(var(--accent))]" />
            Business Health Score
          </h1>
          <p className="text-muted-foreground mt-1">
            AI-powered insights and financial health analysis
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={fetchData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={fetchInsights} disabled={insightsLoading}>
            {insightsLoading ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            Generate AI Insights
          </Button>
        </div>
      </div>

      {/* Risk Alerts Banner */}
      {alerts.filter(a => a.type === 'critical' || a.type === 'warning').length > 0 && (
        <div className="space-y-2">
          {alerts.filter(a => a.type === 'critical' || a.type === 'warning').slice(0, 3).map((alert, idx) => (
            <div 
              key={idx}
              className={`p-4 rounded-lg flex items-start gap-3 ${
                alert.type === 'critical' 
                  ? 'bg-red-500/10 border border-red-500/20' 
                  : 'bg-amber-500/10 border border-amber-500/20'
              }`}
            >
              {getAlertIcon(alert.type)}
              <div className="flex-1">
                <p className="font-medium">{alert.title}</p>
                <p className="text-sm text-muted-foreground">{alert.message}</p>
                <p className="text-xs text-muted-foreground mt-1">{alert.action}</p>
              </div>
              <Badge variant={alert.type === 'critical' ? 'destructive' : 'secondary'}>
                {alert.type}
              </Badge>
            </div>
          ))}
        </div>
      )}

      {/* Main Score Card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Health Score Gauge */}
        <Card className="lg:col-span-1 relative overflow-hidden">
          <div className={`absolute inset-0 bg-gradient-to-br ${getScoreGradient(healthScore.total_score || 0)} opacity-5`} />
          <CardContent className="pt-6 pb-8 flex flex-col items-center justify-center relative">
            <div className="relative">
              {/* Circular Progress */}
              <div className="w-48 h-48 relative">
                <svg className="w-48 h-48 transform -rotate-90">
                  <circle
                    cx="96"
                    cy="96"
                    r="85"
                    stroke="currentColor"
                    strokeWidth="12"
                    fill="none"
                    className="text-muted/30"
                  />
                  <circle
                    cx="96"
                    cy="96"
                    r="85"
                    stroke="currentColor"
                    strokeWidth="12"
                    fill="none"
                    strokeDasharray={`${(healthScore.total_score || 0) * 5.34} 534`}
                    strokeLinecap="round"
                    className={getScoreColor(healthScore.total_score || 0)}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className={`text-5xl font-bold ${getScoreColor(healthScore.total_score || 0)}`}>
                    {healthScore.total_score || 0}
                  </span>
                  <span className="text-sm text-muted-foreground">out of 100</span>
                </div>
              </div>
            </div>
            
            <div className="mt-4 text-center">
              <Badge 
                className={`text-lg px-4 py-1 ${
                  healthScore.status_color === 'emerald' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' :
                  healthScore.status_color === 'blue' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' :
                  healthScore.status_color === 'amber' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                  healthScore.status_color === 'orange' ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' :
                  'bg-red-500/20 text-red-400 border-red-500/30'
                }`}
              >
                {healthScore.status || 'Calculating...'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Score Breakdown */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Score Breakdown
            </CardTitle>
            <CardDescription>How your score is calculated</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {healthScore.breakdown && Object.entries(healthScore.breakdown).map(([key, data]) => (
              <div key={key} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="capitalize font-medium">{key.replace('_', ' ')}</span>
                    <span className="text-xs text-muted-foreground">
                      ({data.value}{key === 'profitability' || key === 'efficiency' || key === 'stability' ? '%' : key === 'growth' ? '%' : ' mo'})
                    </span>
                  </div>
                  <span className="font-bold">{data.score} / {data.max}</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all ${
                      data.score / data.max >= 0.8 ? 'bg-emerald-500' :
                      data.score / data.max >= 0.6 ? 'bg-blue-500' :
                      data.score / data.max >= 0.4 ? 'bg-amber-500' :
                      'bg-red-500'
                    }`}
                    style={{ width: `${(data.score / data.max) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Revenue */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Revenue</p>
                <p className="text-2xl font-bold">{formatCurrency(kpis.revenue?.current)}</p>
                <div className={`flex items-center text-sm mt-1 ${
                  (kpis.revenue?.growth || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {(kpis.revenue?.growth || 0) >= 0 ? (
                    <ArrowUpRight className="w-4 h-4 mr-1" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4 mr-1" />
                  )}
                  {Math.abs(kpis.revenue?.growth || 0).toFixed(1)}% vs last month
                </div>
              </div>
              <div className="p-3 rounded-full bg-emerald-500/10">
                <TrendingUp className="w-6 h-6 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Expenses */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Expenses</p>
                <p className="text-2xl font-bold">{formatCurrency(kpis.expenses?.current)}</p>
                <div className={`flex items-center text-sm mt-1 ${
                  (kpis.expenses?.growth || 0) <= 0 ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {(kpis.expenses?.growth || 0) <= 0 ? (
                    <ArrowDownRight className="w-4 h-4 mr-1" />
                  ) : (
                    <ArrowUpRight className="w-4 h-4 mr-1" />
                  )}
                  {Math.abs(kpis.expenses?.growth || 0).toFixed(1)}% vs last month
                </div>
              </div>
              <div className="p-3 rounded-full bg-red-500/10">
                <TrendingDown className="w-6 h-6 text-red-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Net Profit */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Net Profit</p>
                <p className={`text-2xl font-bold ${(kpis.profit?.net || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {formatCurrency(kpis.profit?.net)}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {kpis.profit?.margin?.toFixed(1)}% margin
                </p>
              </div>
              <div className={`p-3 rounded-full ${(kpis.profit?.net || 0) >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                <DollarSign className={`w-6 h-6 ${(kpis.profit?.net || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Collection Rate */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Collection Rate</p>
                <p className="text-2xl font-bold">{kpis.efficiency?.collection_rate?.toFixed(0) || 0}%</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {kpis.clients?.active || 0} active clients
                </p>
              </div>
              <div className="p-3 rounded-full bg-blue-500/10">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cash Runway Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Banknote className="w-5 h-5" />
            Cash Runway Analysis
          </CardTitle>
          <CardDescription>How long your cash will last at current burn rate</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Current Cash</p>
              <p className="text-3xl font-bold">{formatCurrency(runway.current_cash)}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Monthly Burn Rate</p>
              <p className={`text-3xl font-bold ${runway.burn_rate > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                {runway.burn_rate > 0 ? `-${formatCurrency(runway.burn_rate)}` : formatCurrency(0)}
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Cash Runway</p>
              <p className={`text-3xl font-bold ${
                runway.runway_months === null ? 'text-emerald-400' :
                runway.runway_months >= 6 ? 'text-emerald-400' :
                runway.runway_months >= 3 ? 'text-amber-400' :
                'text-red-400'
              }`}>
                {runway.runway_months === null ? '∞' : `${runway.runway_months?.toFixed(1)} mo`}
              </p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Status</p>
              <Badge className={`text-lg ${
                runway.runway_status === 'Profitable' || runway.runway_status === 'Healthy' 
                  ? 'bg-emerald-500/20 text-emerald-400' :
                runway.runway_status === 'Moderate' 
                  ? 'bg-amber-500/20 text-amber-400' :
                'bg-red-500/20 text-red-400'
              }`}>
                {runway.runway_status || 'Unknown'}
              </Badge>
            </div>
          </div>

          {/* Cash Projection Chart */}
          {runway.projections && runway.projections.length > 0 && (
            <div className="mt-6">
              <p className="text-sm font-medium mb-3">6-Month Cash Projection</p>
              <div className="flex items-end gap-2 h-32">
                {runway.projections.map((proj, idx) => {
                  const maxCash = Math.max(...runway.projections.map(p => Math.abs(p.projected_cash)), 1);
                  const height = Math.abs(proj.projected_cash) / maxCash * 100;
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-xs text-muted-foreground">
                        {formatCurrency(proj.projected_cash)}
                      </span>
                      <div 
                        className={`w-full rounded-t transition-all ${
                          proj.status === 'positive' ? 'bg-emerald-500' : 'bg-red-500'
                        }`}
                        style={{ height: `${Math.max(height, 5)}%` }}
                      />
                      <span className="text-xs text-muted-foreground">M{proj.month}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI Insights Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-400" />
            AI-Powered Insights
          </CardTitle>
          <CardDescription>
            Intelligent recommendations based on your financial data
          </CardDescription>
        </CardHeader>
        <CardContent>
          {insights.length > 0 ? (
            <div className="space-y-4">
              {insights.map((insight, idx) => (
                <div 
                  key={idx}
                  className={`p-4 rounded-lg border ${
                    insight.type === 'warning' ? 'bg-amber-500/5 border-amber-500/20' :
                    insight.type === 'opportunity' ? 'bg-blue-500/5 border-blue-500/20' :
                    insight.type === 'achievement' ? 'bg-emerald-500/5 border-emerald-500/20' :
                    'bg-purple-500/5 border-purple-500/20'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {getInsightIcon(insight.type)}
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold">{insight.title}</h4>
                        <Badge variant="outline" className="text-xs capitalize">
                          {insight.priority} priority
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{insight.description}</p>
                      {insight.action && (
                        <p className="text-sm font-medium mt-2 text-[hsl(var(--accent))]">
                          → {insight.action}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No insights generated yet</p>
              <p className="text-sm mt-1">Click "Generate AI Insights" to get personalized recommendations</p>
              <Button onClick={fetchInsights} className="mt-4" disabled={insightsLoading}>
                {insightsLoading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Generate Insights
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default BusinessHealthScore;
